import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const clearStoredAuth = () => {
  sessionStorage.removeItem('mfu_superadmin_token');
  sessionStorage.removeItem('mfu_superadmin_user');
  sessionStorage.removeItem('mfu_superadmin_login_date');
  sessionStorage.removeItem('mfu_superadmin_otp_verified');
};

const isPageReload = () => {
  const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
  if (navigationEntry) return navigationEntry.type === 'reload';

  // Fallback for older browsers that do not support Navigation Timing Level 2.
  return performance.navigation?.type === 1;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // const API_BASE_URL = 'http://localhost:8000';
  const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';

  useEffect(() => {
    // Remove credentials persisted by older builds so a new tab cannot reuse
    // an authenticated Super Admin session.
    localStorage.removeItem('mfu_superadmin_token');
    localStorage.removeItem('mfu_superadmin_user');

    // Duplicating a tab may clone sessionStorage. Only a real reload is
    // allowed to restore this tab's authenticated or pending OTP session.
    if (!isPageReload()) {
      clearStoredAuth();
      sessionStorage.removeItem('otp_pending');
      sessionStorage.removeItem('otp_verification_token');
    }

    const storedToken = sessionStorage.getItem('mfu_superadmin_token');
    const storedUser = sessionStorage.getItem('mfu_superadmin_user');
    const storedLoginDate = sessionStorage.getItem('mfu_superadmin_login_date');
    const otpVerified = sessionStorage.getItem('mfu_superadmin_otp_verified') === 'true';
    const today = new Date().toDateString();

    if (storedToken && (storedLoginDate !== today || !otpVerified)) {
      clearStoredAuth();
      setLoading(false);
      return;
    }

    if (storedToken && storedUser && otpVerified) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (parseError) {
        console.error('❌ Error parsing stored user data:', parseError);
        clearStoredAuth();
      }
    }

    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.status === 'success') {
        const { user: userData, token: tokenData } = response.data.data;

        // Password verification is only the first step. The authenticated
        // session is created after OTP verification succeeds.
        clearStoredAuth();
        setUser(null);
        setToken(null);

        return { success: true, user: userData, token: tokenData };
      } else {
        return { success: false, message: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
      };
    }
  };

  const completeOtpLogin = (userData, tokenData) => {
    if (!userData || !tokenData) return false;

    setUser(userData);
    setToken(tokenData);
    sessionStorage.setItem('mfu_superadmin_token', tokenData);
    sessionStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));
    sessionStorage.setItem('mfu_superadmin_login_date', new Date().toDateString());
    sessionStorage.setItem('mfu_superadmin_otp_verified', 'true');
    return true;
  };

  const loginWithToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString
      });

      if (response.data.status === 'success' && response.data.data) {
        const userData = response.data.data.user;

        setUser(userData);
        setToken(tokenString);

        sessionStorage.setItem('mfu_superadmin_token', tokenString);
        sessionStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));
        sessionStorage.setItem('mfu_superadmin_login_date', new Date().toDateString());
        sessionStorage.setItem('mfu_superadmin_otp_verified', 'true');

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message || 'Invalid token' };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Token ไม่ถูกต้องหรือหมดอายุ'
      };
    }
  };

  const verifyToken = async (tokenString) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/check-token`, {
        token: tokenString || token
      });

      if (response.data.status !== 'success') {
        throw new Error('Token is invalid');
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearStoredAuth();
    sessionStorage.removeItem('otp_pending');
    sessionStorage.removeItem('otp_verification_token');
  };

  const getProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 'success') {
        const userData = response.data.data.user;
        setUser(userData);
        sessionStorage.setItem('mfu_superadmin_user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  const getUserFullName = () => {
    if (!user) return 'ไม่ระบุ';

    if (user.fullName) return user.fullName;

    const parts = [];
    if (user.nameprefix) parts.push(user.nameprefix);
    if (user.name) parts.push(user.name);
    if (user.lastname) parts.push(user.lastname);

    return parts.length > 0 ? parts.join(' ') : (user.username || 'ไม่ระบุ');
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    completeOtpLogin,
    loginWithToken,
    logout,
    getProfile,
    getUserFullName
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
